/**
 * The pure core of the theme contract (FR-007, contracts/web-surface.md §4):
 * the stored mode plus whether the OS currently prefers dark, resolved to the
 * one scheme the page should render.
 *
 * It lives in `lib/` rather than inside `theme-provider.tsx` so its unit suite
 * never has to load Paper under jsdom; the provider re-exports it, so the
 * contract's "exported from the provider" reading holds too.
 *
 * Deliberately pure — it reads neither `matchMedia` nor `localStorage`. The
 * same rule has to run in two very different places: the React provider, and
 * the inline pre-paint `<head>` script that has no React and must pick the
 * background before the first frame (SC-006). Any environment reading in here
 * would let those two disagree and bring back the wrong-scheme flash, so both
 * callers read their environment themselves and pass the answers in.
 */

/** What a visitor can choose and what is persisted under `localStorage["theme"]`. */
export type ThemeMode = "light" | "dark" | "system";

/** What can actually be rendered — `"system"` is a preference, not a scheme. */
export type ColorScheme = "light" | "dark";

export function resolveScheme(
  mode: ThemeMode,
  systemPrefersDark: boolean,
): ColorScheme {
  if (mode === "system") {
    return systemPrefersDark ? "dark" : "light";
  }

  return mode;
}
